import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';

const FALLBACK_DEBUG_ENDPOINT = 'https://ws.aforo.bink.tools:8080/api/debug/logs';
const STARTUP_DEBUG_FLAG_KEY = 'aforo_startup_debug';
const DEBUG_ENDPOINT_STORAGE_KEY = 'aforo_debug_endpoint';
const DEBUG_SESSION_STORAGE_KEY = 'aforo_debug_session_id';
const APP_VERSION_STORAGE_KEY = 'aforo_app_version';
const STARTUP_FAIL_COUNT_STORAGE_KEY = 'aforo_startup_fail_count';
const SW_RESET_DONE_VERSION_STORAGE_KEY = 'aforo_sw_reset_done_for_version';
const STARTUP_FAILS_BEFORE_SW_RESET = 2;
const RISKY_LOCAL_STORAGE_KEYS = [
  STARTUP_DEBUG_FLAG_KEY,
  DEBUG_ENDPOINT_STORAGE_KEY,
  DEBUG_SESSION_STORAGE_KEY,
  STARTUP_FAIL_COUNT_STORAGE_KEY
];

interface StartupDebugContext {
  enabled: boolean;
  endpoint: string;
  app: {
    appId: string | null;
    appVersion: string | null;
    platform: string | null;
    debugVariant: boolean;
  };
  device: {
    deviceId: string | null;
    deviceName: string | null;
    userName: string | null;
    userDisplayName: string | null;
    os: string | null;
    osVersion: string | null;
    model: string | null;
  };
  sessionId: string;
}

let startupDebugContextPromise: Promise<StartupDebugContext> | null = null;
const startupEpochMs = Date.now();
const startupLaunchId = `launch-${startupEpochMs}-${Math.random().toString(36).slice(2, 8)}`;
let startupSequence = 0;
const startupNavigationType = (() => {
  try {
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    return navEntry?.type || null;
  } catch {
    return null;
  }
})();

function nextStartupSequence(): number {
  startupSequence += 1;
  return startupSequence;
}

function inferStartupPhase(location: unknown, message: unknown): string {
  const combined = `${String(location || '')} ${String(message || '')}`.toLowerCase();
  if (combined.includes('serviceworker') || combined.includes('sw.')) return 'service-worker';
  if (combined.includes('tabs.component.ts')) return 'tabs';
  if (combined.includes('app.component.ts:constructor')) return 'app-constructor';
  if (combined.includes('initializeapp')) return 'app-initialize';
  if (combined.includes('ngoninit')) return 'app-ngoninit';
  if (combined.includes('main.ts')) return 'main';
  return 'startup';
}

function getRuntimeContext(): Record<string, unknown> {
  const context: Record<string, unknown> = {
    online: typeof navigator !== 'undefined' ? navigator.onLine : null,
    readyState: typeof document !== 'undefined' ? document.readyState : null,
    visibilityState: typeof document !== 'undefined' ? document.visibilityState : null,
    href: typeof window !== 'undefined' && window.location ? window.location.href : null
  };

  try {
    const swController = typeof navigator !== 'undefined' && 'serviceWorker' in navigator
      ? navigator.serviceWorker.controller
      : null;
    context['serviceWorkerControlled'] = !!swController;
    context['serviceWorkerControllerUrl'] = swController?.scriptURL || null;
  } catch {
    context['serviceWorkerControlled'] = null;
    context['serviceWorkerControllerUrl'] = null;
  }

  return context;
}

function inferStartupReason(baseData: Record<string, unknown>): string {
  const explicitReason = baseData['startupReason'];
  if (typeof explicitReason === 'string' && explicitReason.trim().length > 0) {
    return explicitReason;
  }

  const launchUrl = baseData['launchUrl'];
  if (typeof launchUrl === 'string' && launchUrl.trim().length > 0) {
    return /(push|notification)/i.test(launchUrl) ? 'from_push' : 'deep_link';
  }

  if (baseData['hasLaunchUrl'] === true) {
    return 'deep_link';
  }

  if (startupNavigationType === 'back_forward') {
    return 'resume';
  }

  return 'cold_start';
}

function getSafeStorageItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function setSafeStorageItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore localStorage errors.
  }
}

function removeSafeStorageItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore localStorage errors.
  }
}

/** Best-effort: drop all SW registrations and Cache Storage (used on native version bumps and main.ts purge). */
export async function unregisterAllServiceWorkersAndCaches(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
  } catch {
    // Ignore
  }
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // Ignore
  }
}

async function resolveCurrentAppVersion(): Promise<string> {
  if (Capacitor.isNativePlatform()) {
    try {
      const info = await App.getInfo();
      return String(info?.version || 'unknown');
    } catch {
      return 'unknown';
    }
  }
  return 'web';
}

async function maybeResetServiceWorkerAndCaches(currentVersion: string): Promise<boolean> {
  const alreadyResetForVersion = getSafeStorageItem(SW_RESET_DONE_VERSION_STORAGE_KEY) === currentVersion;
  if (alreadyResetForVersion) return false;
  const failCount = Number(getSafeStorageItem(STARTUP_FAIL_COUNT_STORAGE_KEY) || '0');
  if (failCount < STARTUP_FAILS_BEFORE_SW_RESET) return false;

  let resetPerformed = false;
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length > 0) {
        await Promise.all(registrations.map((r) => r.unregister()));
        resetPerformed = true;
      }
    }
  } catch {
    // Keep startup resilient if SW APIs fail.
  }

  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      if (keys.length > 0) {
        await Promise.all(keys.map((key) => caches.delete(key)));
        resetPerformed = true;
      }
    }
  } catch {
    // Keep startup resilient if Cache API fails.
  }

  if (resetPerformed) {
    setSafeStorageItem(SW_RESET_DONE_VERSION_STORAGE_KEY, currentVersion);
  }

  return resetPerformed;
}

export async function runStartupSelfHeal(): Promise<void> {
  const currentVersion = await resolveCurrentAppVersion();
  const previousVersion = getSafeStorageItem(APP_VERSION_STORAGE_KEY);
  const versionChanged = !!previousVersion && previousVersion !== currentVersion;

  if (versionChanged) {
    for (const key of RISKY_LOCAL_STORAGE_KEYS) {
      removeSafeStorageItem(key);
    }
    setSafeStorageItem(STARTUP_FAIL_COUNT_STORAGE_KEY, '0');
    removeSafeStorageItem(SW_RESET_DONE_VERSION_STORAGE_KEY);
    if (Capacitor.isNativePlatform()) {
      await unregisterAllServiceWorkersAndCaches();
    }
  }

  setSafeStorageItem(APP_VERSION_STORAGE_KEY, currentVersion);
  const nextFailCount = Number(getSafeStorageItem(STARTUP_FAIL_COUNT_STORAGE_KEY) || '0') + 1;
  setSafeStorageItem(STARTUP_FAIL_COUNT_STORAGE_KEY, String(nextFailCount));

  await maybeResetServiceWorkerAndCaches(currentVersion);
}

export function markStartupHealthy(): void {
  setSafeStorageItem(STARTUP_FAIL_COUNT_STORAGE_KEY, '0');
}

function mirrorStartupToLocalRequest(seq: number, tag: string, message: string): void {
  try {
    const url = new URL('https://localhost/__aforo_startup__');
    url.searchParams.set('seq', String(seq));
    url.searchParams.set('tag', tag.slice(0, 64));
    url.searchParams.set('msg', message.slice(0, 120));
    // Fire-and-forget local request so Capacitor emits a visible "Handling local request" line in logcat.
    fetch(url.toString(), { method: 'GET' }).catch(() => {});
  } catch {
    // Ignore local mirror errors to keep startup resilient.
  }
}

export function getOrCreateDebugSessionId(): string {
  const inMemoryFallback = `aforo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  try {
    const existing = localStorage.getItem(DEBUG_SESSION_STORAGE_KEY);
    if (existing) return existing;
    localStorage.setItem(DEBUG_SESSION_STORAGE_KEY, inMemoryFallback);
    return inMemoryFallback;
  } catch {
    return inMemoryFallback;
  }
}

async function resolveStartupDebugContext(): Promise<StartupDebugContext> {
  let enabled = false;
  let endpoint = FALLBACK_DEBUG_ENDPOINT;
  let appId: string | null = null;
  let appVersion: string | null = null;
  const platform = Capacitor.getPlatform();
  let debugVariant = false;

  try {
    if (localStorage.getItem(STARTUP_DEBUG_FLAG_KEY) === '1') {
      enabled = true;
    }
    if (enabled) {
      endpoint = localStorage.getItem(DEBUG_ENDPOINT_STORAGE_KEY) || FALLBACK_DEBUG_ENDPOINT;
    }
  } catch {
    // Keep defaults when localStorage is not available.
  }

  if (Capacitor.isNativePlatform()) {
    try {
      const appInfo = await App.getInfo();
      appId = appInfo?.id || null;
      appVersion = appInfo?.version || null;
      const normalizedAppId = String(appInfo?.id || '').toLowerCase();
      const normalizedVersion = String(appInfo?.version || '').toLowerCase();
      debugVariant = normalizedAppId.endsWith('.debug') || normalizedVersion.includes('debug');
      if (enabled) {
        try {
          endpoint = localStorage.getItem(DEBUG_ENDPOINT_STORAGE_KEY) || FALLBACK_DEBUG_ENDPOINT;
        } catch {
          endpoint = FALLBACK_DEBUG_ENDPOINT;
        }
      }
    } catch {
      // Keep already derived values.
    }
  }

  let device = {
    deviceId: null as string | null,
    deviceName: null as string | null,
    userName: null as string | null,
    userDisplayName: null as string | null,
    os: null as string | null,
    osVersion: null as string | null,
    model: null as string | null
  };

  if (enabled && Capacitor.isNativePlatform()) {
    try {
      const [deviceInfo, deviceId] = await Promise.all([
        Device.getInfo(),
        Device.getId()
      ]);
      device = {
        deviceId: deviceId?.identifier || null,
        deviceName: deviceInfo?.name || null,
        userName: null,
        userDisplayName: null,
        os: deviceInfo?.operatingSystem || null,
        osVersion: deviceInfo?.osVersion || null,
        model: deviceInfo?.model || null
      };
    } catch {
      // Keep null device metadata if unavailable.
    }
  }

  return {
    enabled,
    endpoint,
    app: {
      appId,
      appVersion,
      platform: platform || null,
      debugVariant
    },
    device,
    sessionId: enabled ? getOrCreateDebugSessionId() : ''
  };
}

function getStartupDebugContext(): Promise<StartupDebugContext> {
  if (!startupDebugContextPromise) {
    startupDebugContextPromise = resolveStartupDebugContext();
  }
  return startupDebugContextPromise;
}

export function postStartupDebug(payload: Record<string, unknown>): void {
  getStartupDebugContext().then((context) => {
    if (!context.enabled) return;

    const seq = nextStartupSequence();
    const hypothesisId = String(payload['hypothesisId'] || '').trim();
    const fallbackTag = hypothesisId || 'Startup';
    const tag = String(payload['tag'] || fallbackTag);
    const message = String(payload['message'] || 'Startup debug event');
    const location = payload['location'];
    const startupPhase = String(payload['startupPhase'] || inferStartupPhase(location, message));
    const baseData = typeof payload['data'] === 'object' && payload['data']
      ? payload['data'] as Record<string, unknown>
      : {};
    const startupReason = inferStartupReason(baseData);
    const data = {
      ...baseData,
      location,
      runId: payload['runId'],
      hypothesisId: payload['hypothesisId'],
      launchId: startupLaunchId,
      startupPhase,
      startupReason,
      startupNavigationType,
      seq,
      elapsedMs: Date.now() - startupEpochMs,
      runtime: getRuntimeContext()
    };

    const timestampValue = payload['timestamp'];
    const timestamp = typeof timestampValue === 'number' ? timestampValue : Date.now();

    // Mirror startup breadcrumbs to logcat for on-device diagnosis when network upload is unavailable.
    try {
      console.error('[AFORO_STARTUP]', JSON.stringify({
        timestamp,
        tag,
        message,
        sessionId: context.sessionId,
        data
      }));
    } catch {
      // Keep startup flow resilient if stringification fails.
    }
    mirrorStartupToLocalRequest(seq, tag, message);

    fetch(context.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Debug-Session-Id': context.sessionId
      },
      body: JSON.stringify({
        app: context.app,
        session: {
          sessionId: context.sessionId
        },
        device: context.device,
        log: {
          timestamp,
          level: 'info',
          tag,
          message,
          data
        }
      })
    }).catch(() => {});
  });
}
